import {
  buildCalendarTaskFilterDocument,
  calendarFilterOptions,
  dedupeTasksById,
  filterTasksClientSide,
  getCalendarTaskWindow,
} from '@griply/utils';
import { addDays } from 'date-fns';
import {
  useCompletedTasksInRange,
  useFilterDocumentFilters,
  useTasksByFilterDocument,
} from 'features/tasks';
import { useMemo } from 'react';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';

import { useUpcomingFilterDocument } from './use-upcoming-filter-document';

export const useCalendarTasks = (
  startDate: Timestamp,
  dayRange: number,
  bufferWeeks = 2,
): Task[] => {
  // The calendar honours the Upcoming filters but supplies its own date window: `or` date branches dropped, uncompleted-only.
  const upcomingFilterDocument = useUpcomingFilterDocument();
  const { start, end } = getCalendarTaskWindow(
    startDate,
    addDays(startDate, dayRange - 1),
    bufferWeeks,
  );
  const startMs = start.getTime();
  const endMs = end.getTime();
  const filterDocument = useMemo(
    () =>
      buildCalendarTaskFilterDocument(
        { ...upcomingFilterDocument, or: null, showCompleted: false },
        {
          start: new Date(startMs),
          end: new Date(endMs),
        },
      ),
    [upcomingFilterDocument, startMs, endMs],
  );

  const windowedUncompleted = useTasksByFilterDocument(
    filterDocument,
    calendarFilterOptions,
  );

  const completedRange = useMemo(
    () => ({ start: new Date(startMs), end: new Date(endMs) }),
    [startMs, endMs],
  );
  const allCompleted = useCompletedTasksInRange(
    completedRange.start,
    completedRange.end,
  );

  // The completed read is a plain range query, so an active Upcoming filter is applied client-side to match the windowed read.
  const completedFilterDocument = useMemo(
    () => ({ ...upcomingFilterDocument, or: null }),
    [upcomingFilterDocument],
  );
  const completedFilters = useFilterDocumentFilters(completedFilterDocument, {
    includeArchived: true,
  });
  const completed = useMemo(() => {
    const hasActiveFilters = Object.values(
      upcomingFilterDocument.filters ?? {},
    ).some(Boolean);
    return hasActiveFilters
      ? filterTasksClientSide(allCompleted, completedFilters)
      : allCompleted;
  }, [upcomingFilterDocument.filters, allCompleted, completedFilters]);

  return useMemo(
    () => dedupeTasksById(windowedUncompleted, completed),
    [windowedUncompleted, completed],
  );
};
